import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  Loader2,
  FileText,
  Copy,
  Download,
  ChevronDown,
  ChevronRight,
  Code,
  Info,
  GitCompare,
  Eye,
  MessageSquare,
  X,
  Trash2,
  Cloud,
  ExternalLink,
  Terminal,
  FileCode,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import { apiClient, RiskFinding, CloudUniqueFinding } from '@/api/client';
import { ModeToggle } from '@/components/mode-toggle';
import { FindingChat } from '@/components/FindingChat';
import { CloudFindingChat } from '@/components/CloudFindingChat';
import * as Diff from 'diff';
import { MobileMenuButton } from '@/components/MobileMenuButton';

// Create GitHub-style diff using Myers algorithm
const createDiff = (oldText: string, newText: string): Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> => {
  const changes = Diff.diffLines(oldText, newText);
  const result: Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> = [];

  let oldLineNumber = 1;
  let newLineNumber = 1;

  changes.forEach(change => {
    const lines = change.value.split('\n');
    // Remove the last empty line if it exists (common with diffLines)
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    lines.forEach(line => {
      if (change.removed) {
        result.push({
          line,
          type: 'removed',
          lineNumber: oldLineNumber++
        });
      } else if (change.added) {
        result.push({
          line,
          type: 'added',
          lineNumber: newLineNumber++
        });
      } else {
        result.push({
          line,
          type: 'unchanged',
          lineNumber: oldLineNumber
        });
        oldLineNumber++;
        newLineNumber++;
      }
    });
  });

  return result;
};

const RiskAssessmentFindingDetail: React.FC = () => {
  const { assessmentId, findingId } = useParams<{ assessmentId?: string; findingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect if this is a cloud finding based on route
  const isCloudFinding = location.pathname.startsWith('/cloud-findings');

  const [finding, setFinding] = useState<RiskFinding | null>(null);
  const [cloudFinding, setCloudFinding] = useState<CloudUniqueFinding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([1])); // Expand first step by default
  const [copiedStep, setCopiedStep] = useState<number | null>(null);
  const [showDiffView, setShowDiffView] = useState<Set<number>>(new Set()); // Track which steps show diff view
  const [expandedCloudSteps, setExpandedCloudSteps] = useState<Set<number>>(new Set([1])); // Expand first cloud step by default
  const [copiedCloudStep, setCopiedCloudStep] = useState<number | null>(null);
  const [showChat, setShowChat] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const [chatMounted, setChatMounted] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const [isDismissing, setIsDismissing] = useState(false);

  const loadFindingDetails = useCallback(async () => {
    if (!findingId) return;

    setIsLoading(true);
    setError('');
    try {
      if (isCloudFinding) {
        // Load cloud finding
        const cloudFindingDetails = await apiClient.getCloudUniqueFindingById(findingId);
        setCloudFinding(cloudFindingDetails);
      } else {
        // Load K8s finding - use different API endpoint depending on whether we have assessmentId
        const findingDetails = assessmentId
          ? await apiClient.getRiskAssessmentFinding(assessmentId, findingId)
          : await apiClient.getUniqueFinding(findingId);
        setFinding(findingDetails);
      }
    } catch (err: any) {
      console.error('Failed to load finding details:', err);
      setError(err.message || 'Failed to load finding details');
    } finally {
      setIsLoading(false);
    }
  }, [assessmentId, findingId, isCloudFinding]);

  useEffect(() => {
    if (findingId) {
      loadFindingDetails();
    }
  }, [findingId, loadFindingDetails]);

  const handleDismissFinding = async () => {
    if (!findingId) return;

    setIsDismissing(true);
    setError('');
    try {
      if (isCloudFinding) {
        // Dismiss cloud finding
        await apiClient.dismissCloudUniqueFinding(findingId, 'Manually dismissed from finding detail page');
      } else if (assessmentId) {
        // Dismiss from specific assessment
        await apiClient.dismissFinding(assessmentId, findingId);
      } else {
        // Delete unique finding
        await apiClient.deleteUniqueFinding(findingId);
      }
      // Always navigate back to vulnerability dashboard
      navigate('/vulnerability-dashboard');
    } catch (err: any) {
      console.error('Failed to dismiss finding:', err);
      setError('Failed to dismiss finding: ' + (err.message || 'Unknown error'));
      setIsDismissing(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
      case 'low': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'medium': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'low': return <Info className="h-4 w-4 text-yellow-600" />;
      default: return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType) {
      case 'create': return <FileText className="h-4 w-4 text-green-600" />;
      case 'update': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'delete': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getFileTypeBadge = (fileType: string) => {
    switch (fileType) {
      case 'create': return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">Create</Badge>;
      case 'update': return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">Update</Badge>;
      case 'delete': return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const toggleStepExpansion = (stepNumber: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepNumber)) {
        newSet.delete(stepNumber);
      } else {
        newSet.add(stepNumber);
      }
      return newSet;
    });
  };

  const toggleDiffView = (stepNumber: number) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepNumber)) {
        newSet.delete(stepNumber);
      } else {
        newSet.add(stepNumber);
      }
      return newSet;
    });
  };

  const copyYamlToClipboard = async (yamlContent: string, stepNumber: number) => {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopiedStep(stepNumber);
      setTimeout(() => setCopiedStep(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadYamlFile = (yamlContent: string, filePath: string) => {
    const blob = new Blob([yamlContent], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from path, or use a default
    const filename = filePath.split('/').pop() || 'remediation.yaml';
    link.setAttribute('download', filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  // Render Cloud Finding
  if (isCloudFinding && cloudFinding) {
    const canDismiss = cloudFinding.status === 'open';

    return (
      <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -my-4 overflow-hidden" style={{ height: '100vh' }}>
        {/* Page Header — fixed at top, consistent with K8s finding page */}
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 border-b border-border bg-background">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <MobileMenuButton />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-foreground">Cloud Security Finding</h1>
                  {getSeverityIcon(cloudFinding.severity)}
                  <Badge className={cn("capitalize whitespace-nowrap", getSeverityColor(cloudFinding.severity))}>
                    {cloudFinding.severity} Risk
                  </Badge>
                  <Badge variant={cloudFinding.status === 'open' ? 'outline' : 'secondary'} className="capitalize">
                    {cloudFinding.status}
                  </Badge>
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  <span className="flex items-center gap-1">
                    <Code className="h-3.5 w-3.5" />
                    {cloudFinding.category}
                    <span className="mx-1">·</span>
                    {cloudFinding.cloud_provider === 'oci'
                      ? `OCI Tenancy: ${cloudFinding.oci_tenancy_ocid
                          ? (cloudFinding.oci_tenancy_ocid.length > 30
                              ? `${cloudFinding.oci_tenancy_ocid.substring(0, 30)}…`
                              : cloudFinding.oci_tenancy_ocid)
                          : 'Unknown'}`
                      : `AWS Account: ${cloudFinding.aws_account_id || 'Unknown'}`}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {canDismiss && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDismissFinding}
                  disabled={isDismissing}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDismissing ? 'Dismissing...' : 'Dismiss Finding'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!showChat) setChatMounted(true);
                  setShowChat(!showChat);
                }}
                className="flex items-center gap-2"
              >
                {showChat ? (
                  <>
                    <X className="h-4 w-4" />
                    Hide Chat
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4" />
                    Show Chat
                  </>
                )}
              </Button>
              <ModeToggle />
            </div>
          </div>
        </div>

        {/* Content area — main + chat side by side */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-6 min-w-0">
          <div className={cn("space-y-6", showChat ? "md:pr-1" : "")}>

            {/* Finding Overview */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {getSeverityIcon(cloudFinding.severity)}
                  {cloudFinding.title}
                </h3>
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground">{cloudFinding.description}</p>
                </div>

                {cloudFinding.recommendation && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">Recommendation</h4>
                    <p className="text-sm text-muted-foreground">{cloudFinding.recommendation}</p>
                  </div>
                )}

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Affected Resources</p>
                    <p className="text-2xl font-bold">{cloudFinding.affected_resources_count}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Occurrences</p>
                    <p className="text-2xl font-bold">{cloudFinding.occurrence_count}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">First Seen</p>
                    <p className="text-sm font-medium">{new Date(cloudFinding.first_seen_at).toLocaleDateString()}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Last Seen</p>
                    <p className="text-sm font-medium">{new Date(cloudFinding.last_seen_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Dismissed Info */}
                {cloudFinding.status === 'dismissed' && cloudFinding.dismissed_at && (
                  <Alert className="mt-4">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Dismissed on {new Date(cloudFinding.dismissed_at).toLocaleString()}
                      {cloudFinding.dismissed_reason && (
                        <span> - Reason: {cloudFinding.dismissed_reason}</span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Remediation Steps for Cloud */}
            {cloudFinding.has_remediation_steps && cloudFinding.remediation_steps && Array.isArray(cloudFinding.remediation_steps) && cloudFinding.remediation_steps.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-px h-8 bg-gradient-to-b from-amber-500 to-orange-500"></div>
                  <h3 className="text-lg font-semibold text-foreground">Remediation Steps</h3>
                  <Badge variant="outline" className="ml-2">
                    {cloudFinding.remediation_steps.length} {cloudFinding.remediation_steps.length === 1 ? 'step' : 'steps'}
                  </Badge>
                </div>

                <div className="space-y-4">
                  {[...cloudFinding.remediation_steps]
                    .sort((a: any, b: any) => (a.step_number || 0) - (b.step_number || 0))
                    .map((step: any, index: number) => {
                      const stepNumber = step.step_number || index + 1;
                      const fixType = step.fix_type || 'aws_cli';
                      const isExpanded = expandedCloudSteps.has(stepNumber);
                      const isCopied = copiedCloudStep === stepNumber;

                      const getFixTypeIcon = (type: string) => {
                        switch (type) {
                          case 'terraform':
                            return <FileCode className="h-4 w-4 text-purple-500" />;
                          case 'cloudformation':
                            return <Database className="h-4 w-4 text-orange-500" />;
                          case 'pulumi':
                            return <Code className="h-4 w-4 text-blue-500" />;
                          case 'aws_cli':
                          default:
                            return <Terminal className="h-4 w-4 text-green-500" />;
                        }
                      };

                      const getFixTypeBadge = (type: string) => {
                        const variants: Record<string, string> = {
                          'aws_cli': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                          'terraform': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
                          'cloudformation': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
                          'pulumi': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
                        };
                        const labels: Record<string, string> = {
                          'aws_cli': 'AWS CLI',
                          'terraform': 'Terraform',
                          'cloudformation': 'CloudFormation',
                          'pulumi': 'Pulumi',
                        };
                        return (
                          <Badge className={cn("text-xs", variants[type] || variants['aws_cli'])}>
                            {labels[type] || 'AWS CLI'}
                          </Badge>
                        );
                      };

                      const getActionBadge = (action: string) => {
                        const variants: Record<string, string> = {
                          'create': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                          'update': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
                          'delete': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                        };
                        return action ? (
                          <Badge className={cn("text-xs", variants[action] || 'bg-gray-100 text-gray-800')}>
                            {action}
                          </Badge>
                        ) : null;
                      };

                      const copyContentToClipboard = async (content: string, stepNum: number) => {
                        try {
                          await navigator.clipboard.writeText(content);
                          setCopiedCloudStep(stepNum);
                          setTimeout(() => setCopiedCloudStep(null), 2000);
                        } catch (err) {
                          console.error('Failed to copy:', err);
                        }
                      };

                      return (
                        <Card key={stepNumber} className="border-l-4 border-l-amber-500">
                          <CardHeader className="pb-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-semibold text-sm flex-shrink-0">
                                  {stepNumber}
                                </div>
                                <div className="min-w-0">
                                  <CardTitle className="text-base break-words">{step.description || 'Remediation Step'}</CardTitle>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {getFixTypeIcon(fixType)}
                                    {step.aws_service && (
                                      <span className="text-sm text-muted-foreground">{step.aws_service}</span>
                                    )}
                                    {getFixTypeBadge(fixType)}
                                    {getActionBadge(step.action)}
                                    {step.resource_type && (
                                      <Badge variant="outline" className="text-xs">
                                        {step.resource_type}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {step.content && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => copyContentToClipboard(step.content, stepNumber)}
                                    className="text-xs"
                                  >
                                    <Copy className="h-3 w-3 mr-1" />
                                    {isCopied ? 'Copied!' : 'Copy'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Resource Info */}
                            {(step.resource_name || step.resource_arn) && (
                              <div className="flex items-start gap-2 text-sm min-w-0">
                                <Cloud className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div className="text-muted-foreground min-w-0">
                                  {step.resource_name && <span className="font-medium">{step.resource_name}</span>}
                                  {step.resource_arn && (
                                    <span className="text-xs block mt-1 font-mono bg-muted px-1 rounded break-all">{step.resource_arn}</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Explanation */}
                            {step.explanation && (
                              <div>
                                <h5 className="font-medium text-foreground mb-2">Why this fixes the issue:</h5>
                                <p className="text-sm text-muted-foreground">{step.explanation}</p>
                              </div>
                            )}

                            {step.content && (
                              <>
                                <Separator />

                                {/* Content */}
                                <Collapsible
                                  open={isExpanded}
                                  onOpenChange={() => {
                                    setExpandedCloudSteps(prev => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(stepNumber)) {
                                        newSet.delete(stepNumber);
                                      } else {
                                        newSet.add(stepNumber);
                                      }
                                      return newSet;
                                    });
                                  }}
                                >
                                  <CollapsibleTrigger asChild>
                                    <Button variant="outline" className="w-full justify-between">
                                      <span className="flex items-center gap-2">
                                        {getFixTypeIcon(fixType)}
                                        View {fixType === 'aws_cli' ? 'Command' : 'Code'}
                                      </span>
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="mt-4">
                                    <div className="relative">
                                      <pre className="bg-muted/50 p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap break-all">
                                        {step.content}
                                      </pre>
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Affected Resources */}
            {cloudFinding.affected_resources && cloudFinding.affected_resources.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Affected Resources ({cloudFinding.affected_resources.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {cloudFinding.affected_resources.map((resource, index) => (
                      <div key={index} className="flex items-start justify-between gap-2 p-3 border rounded-lg">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium break-words">{resource.name}</p>
                          <p className="text-sm text-muted-foreground">{resource.resource_type}</p>
                          {resource.arn && (
                            <p className="text-xs text-muted-foreground font-mono break-all">{resource.arn}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="flex-shrink-0">{resource.resource_type}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cloud Account Information - AWS or OCI */}
            <Card className={cn(
              "border",
              cloudFinding.cloud_provider === 'oci'
                ? "bg-red-50/50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                : "bg-amber-50/50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
            )}>
              <CardHeader>
                <CardTitle className={cn(
                  "flex items-center gap-2",
                  cloudFinding.cloud_provider === 'oci'
                    ? "text-red-700 dark:text-red-300"
                    : "text-amber-700 dark:text-amber-300"
                )}>
                  <Cloud className="h-5 w-5" />
                  {cloudFinding.cloud_provider === 'oci' ? 'OCI Tenancy Information' : 'AWS Account Information'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cloudFinding.cloud_provider === 'oci' ? (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">OCI Tenancy OCID</p>
                        <p className="font-mono text-xs text-red-600 dark:text-red-400 break-all">{cloudFinding.oci_tenancy_ocid || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Connection ID</p>
                        <p className="font-mono text-sm text-red-600 dark:text-red-400">{cloudFinding.oci_connection_id || 'Unknown'}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">AWS Account ID</p>
                        <p className="font-mono text-amber-600 dark:text-amber-400">{cloudFinding.aws_account_id || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Connection ID</p>
                        <p className="font-mono text-sm text-amber-600 dark:text-amber-400">{cloudFinding.aws_connection_id || 'Unknown'}</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Chat Interface - Right Side */}
        {chatMounted && (
          <div className={cn(
            "w-full md:w-[41%] border-t md:border-t-0 md:border-l border-border/50 bg-card/30 min-w-0 overflow-hidden",
            !showChat && "hidden"
          )}>
            <CloudFindingChat finding={cloudFinding} />
          </div>
        )}
        </div>
      </div>
    );
  }

  // Render K8s Finding (original implementation)
  if (!finding) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Finding Not Found</h2>
          <p className="text-gray-600 mb-4">The requested security finding could not be found.</p>
          <Button onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -my-4 overflow-hidden" style={{ height: '100vh' }}>
      {/* Page Header — fixed at top, consistent with other pages */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <MobileMenuButton />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">Security Finding Remediation</h1>
                {getSeverityIcon(finding.severity)}
                <Badge className={cn("capitalize whitespace-nowrap", getSeverityColor(finding.severity))}>
                  {finding.severity} Risk
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                <span className="flex items-center gap-1">
                  <Code className="h-3.5 w-3.5" />
                  {finding.category}
                  {finding.cluster_name && (
                    <>
                      <span className="mx-1">·</span>
                      Cluster: {finding.cluster_name}
                    </>
                  )}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDismissFinding}
              disabled={isDismissing}
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {isDismissing ? 'Dismissing...' : 'Dismiss Finding'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowChat(!showChat)}
              className="flex items-center gap-2"
            >
              {showChat ? (
                <>
                  <X className="h-4 w-4" />
                  Hide Chat
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  Show Chat
                </>
              )}
            </Button>
            <ModeToggle />
          </div>
        </div>
      </div>

      {/* Content area — main + chat side by side */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-6 min-w-0">
        <div className={cn("space-y-6", showChat ? "md:pr-1" : "")}>

      {/* Finding Overview */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {getSeverityIcon(finding.severity)}
            {finding.title}
          </h3>
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Description</h4>
            <p className="text-sm text-muted-foreground">{finding.description}</p>
          </div>

          {finding.recommendation && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-1">Recommendation</h4>
              <p className="text-sm text-muted-foreground">{finding.recommendation}</p>
            </div>
          )}

          {finding.affected_resources && finding.affected_resources.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Affected Resources</h4>
              <div className="flex flex-wrap gap-2">
                {finding.affected_resources.map((resource, index) => (
                  <SimpleTooltip
                    key={index}
                    content={resource.resource_type ? `${resource.resource_type}${resource.namespace ? ` in ${resource.namespace}` : ' (cluster-scoped)'}` : 'Unknown resource type'}
                  >
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-help hover:bg-secondary/80 transition-colors"
                    >
                      {resource.namespace ? `${resource.namespace}/${resource.name}` : resource.name}
                    </Badge>
                  </SimpleTooltip>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Remediation Steps */}
      {finding.remediation_steps && finding.remediation_steps.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-px h-8 bg-gradient-to-b from-green-500 to-blue-500"></div>
            <h3 className="text-lg font-semibold text-foreground">Remediation Steps</h3>
            <Badge variant="outline" className="ml-2">
              {finding.remediation_steps.length} {finding.remediation_steps.length === 1 ? 'step' : 'steps'}
            </Badge>
          </div>

          <div className="space-y-4">
            {finding.remediation_steps
              .sort((a, b) => a.step_number - b.step_number)
              .map((step) => {
                const isExpanded = expandedSteps.has(step.step_number);
                const isCopied = copiedStep === step.step_number;

                return (
                  <Card key={step.step_number} className="border-l-4 border-l-green-500">
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-semibold text-sm flex-shrink-0">
                            {step.step_number}
                          </div>
                          <div className="min-w-0">
                            <CardTitle className="text-base break-words">{step.description}</CardTitle>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {getFileTypeIcon(step.file_type)}
                              <span className="text-sm text-muted-foreground truncate">{step.file_path}</span>
                              {getFileTypeBadge(step.file_type)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyYamlToClipboard(step.yaml_content, step.step_number)}
                            className="text-xs"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {isCopied ? 'Copied!' : 'Copy'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadYamlFile(step.yaml_content, step.file_path)}
                            className="text-xs"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Explanation */}
                      <div>
                        <h5 className="font-medium text-foreground mb-2">Why this fixes the issue:</h5>
                        <p className="text-sm text-muted-foreground">{step.explanation}</p>
                      </div>

                      <Separator />

                      {/* YAML Content */}
                      <Collapsible open={isExpanded} onOpenChange={() => toggleStepExpansion(step.step_number)}>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" className="w-full justify-between">
                            <span className="flex items-center gap-2">
                              <Code className="h-4 w-4" />
                              View YAML Configuration
                            </span>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4">
                          <div className="space-y-4">
                            {/* Toggle between single view and diff view for updates */}
                            {step.existing_yaml && step.file_type === 'update' && (
                              <div className="flex justify-center">
                                <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                                  <Button
                                    variant={!showDiffView.has(step.step_number) ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setShowDiffView(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(step.step_number);
                                      return newSet;
                                    })}
                                    className="text-xs h-7"
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    New YAML
                                  </Button>
                                  <Button
                                    variant={showDiffView.has(step.step_number) ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => toggleDiffView(step.step_number)}
                                    className="text-xs h-7"
                                  >
                                    <GitCompare className="h-3 w-3 mr-1" />
                                    Show Diff
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* YAML Content Display */}
                            {step.existing_yaml && step.file_type === 'update' && showDiffView.has(step.step_number) ? (
                              // Unified diff view for updates (GitHub-style)
                              <div>
                                <div className="flex justify-end mb-2">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => copyYamlToClipboard(step.yaml_content, step.step_number)}
                                      className="text-xs h-6"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => downloadYamlFile(step.yaml_content, step.file_path)}
                                      className="text-xs h-6"
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="bg-muted p-3 rounded-md border font-mono text-sm overflow-x-auto max-h-96">
                                  {createDiff(step.existing_yaml, step.yaml_content).map((diffLine, lineIndex) => (
                                    <div
                                      key={lineIndex}
                                      className={cn(
                                        "whitespace-pre-wrap flex",
                                        diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
                                        diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
                                        diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
                                      )}
                                    >
                                      <span className={cn(
                                        "inline-block w-8 text-right mr-3 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-2",
                                        diffLine.type === 'removed' && "text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30",
                                        diffLine.type === 'added' && "text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30"
                                      )}>
                                        {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                                      </span>
                                      <span className="flex-1 min-w-0">
                                        {diffLine.line}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              // Single view (for creates, deletes, or when not in diff mode)
                              <div className="relative">
                                <pre className={cn(
                                  "p-4 rounded-md border font-mono text-sm overflow-x-auto",
                                  step.file_type === 'delete'
                                    ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                                    : step.file_type === 'create'
                                    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                                    : "bg-muted"
                                )}>
                                  <code className="text-foreground">
                                    {step.file_type === 'delete' && step.existing_yaml
                                      ? step.existing_yaml
                                      : step.yaml_content}
                                  </code>
                                </pre>
                                <div className="absolute top-2 right-2 flex gap-1">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => copyYamlToClipboard(
                                      step.file_type === 'delete' && step.existing_yaml
                                        ? step.existing_yaml
                                        : step.yaml_content,
                                      step.step_number
                                    )}
                                    className="text-xs h-7"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                  {step.file_type !== 'delete' && (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => downloadYamlFile(step.yaml_content, step.file_path)}
                                      className="text-xs h-7"
                                    >
                                      <Download className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Resource Information */}
                            {(step.resource_name || step.resource_kind || step.resource_namespace) && (
                              <div className="mt-3 p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                                <h6 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Resource Information</h6>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  {step.resource_kind && (
                                    <div>
                                      <span className="text-muted-foreground">Kind:</span>
                                      <div className="font-mono text-blue-600 dark:text-blue-400">{step.resource_kind}</div>
                                    </div>
                                  )}
                                  {step.resource_name && (
                                    <div>
                                      <span className="text-muted-foreground">Name:</span>
                                      <div className="font-mono text-blue-600 dark:text-blue-400">{step.resource_name}</div>
                                    </div>
                                  )}
                                  {step.resource_namespace && (
                                    <div>
                                      <span className="text-muted-foreground">Namespace:</span>
                                      <div className="font-mono text-blue-600 dark:text-blue-400">{step.resource_namespace}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </CardContent>
                  </Card>
                );
              })}
          </div>

        </div>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Automated Remediation Steps Available</h3>
            <p className="text-muted-foreground mb-4">
              Automated YAML remediation steps could not be generated for this finding.
              Please refer to the recommendation above for manual remediation guidance.
            </p>
            <Alert className="text-left">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Manual Remediation Required:</strong> Review the recommendation section above and implement
                appropriate security controls based on your specific environment and requirements.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
        </div>
      </div>

      {/* Chat Interface - Right Side */}
      {chatMounted && (
        <div className={cn(
          "w-full md:w-[41%] border-t md:border-t-0 md:border-l border-border/50 bg-card/30 min-w-0 overflow-hidden",
          !showChat && "hidden"
        )}>
          <FindingChat
            finding={finding}
            assessmentId={assessmentId!}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default RiskAssessmentFindingDetail;
